import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import api from '../api/api'
import { fetchExchangeRates } from '../utils/currencyUtils'

const STORAGE_KEY = 'omah_detected_currency'

// Module-level set of state setters — shared across ALL useCurrency instances
const listeners = new Set()

const broadcast = (code) => {
    listeners.forEach(fn => fn(code))
}

/**
 * Returns the active currency code, a setter, and whether exchange rates have loaded.
 * Priority: logged-in user's account currency > localStorage > NGN fallback.
 * All useCurrency instances in the same tab stay in sync via the module-level listener set.
 */
export const useCurrency = () => {
    const { userInfo } = useSelector(state => state.auth)

    const [currencyCode, setCurrencyCodeState] = useState(
        () => localStorage.getItem(STORAGE_KEY) || 'NGN'
    )
    const [exchangeLoaded, setExchangeLoaded] = useState(false)

    useEffect(() => {
        fetchExchangeRates().then(() => setExchangeLoaded(true))
    }, [])

    // Load user's saved currency preference on login
    useEffect(() => {
        if (userInfo?.id) {
            api.get('/account/get-details', { withCredentials: true })
                .then(({ data }) => {
                    if (data.currency) {
                        localStorage.setItem(STORAGE_KEY, data.currency)
                        broadcast(data.currency)
                    }
                })
                .catch(() => {})
        } else {
            const stored = localStorage.getItem(STORAGE_KEY) || 'NGN'
            broadcast(stored)
        }
    }, [userInfo])

    // Register this instance's setter so it receives broadcasts from any other instance
    useEffect(() => {
        listeners.add(setCurrencyCodeState)
        return () => listeners.delete(setCurrencyCodeState)
    }, [])

    const setCurrencyCode = (code) => {
        localStorage.setItem(STORAGE_KEY, code)
        broadcast(code) // Updates ALL mounted useCurrency instances including this one
        if (userInfo?.id) {
            api.put('/account/update-info', { currency: code }, { withCredentials: true }).catch(() => {})
        }
    }

    return { currencyCode, setCurrencyCode, exchangeLoaded }
}
